import { Box, styled } from '@mui/material';
import { useRecoilState, useRecoilValue } from 'recoil';
import PlaneViewFrame from '~/components/Viewer/Views/PlaneViewFrame';
import CoordinateDisplay from '~/containers/viewer/viewport/ViewportOverlay/CoordinateDisplay';
import CrosshairsComponent from '~/containers/viewer/viewport/ViewportOverlay/Crosshairs/index';
import DoseColorwashSlider from '~/containers/viewer/viewport/ViewportOverlay/DoseColorwashSlider';
import LabelDirections from '~/containers/viewer/viewport/ViewportOverlay/LabelDirections';
import SwapToFullScreen from '~/containers/viewer/viewport/ViewportOverlay/SwapToFullScreen';
import SwapToMain from '~/containers/viewer/viewport/ViewportOverlay/SwapToMain';
import {
    Orientations,
    PlaneView,
    PlaneViewType,
} from '~/engine/view/views/PlaneView';
import usePetImage from '~/hooks/image/usePetImage';
import { doseVisibilityState } from '~/state/atoms/dose/doseAtoms';
import { scaleDisplayEnabledState } from '~/state/atoms/toolsAtoms';
import {
    GridLayoutMode,
    gridLayoutModeState,
    showFullscreenState,
    viewportOrientationOrderState,
} from '~/state/atoms/viewerAtoms';
import ImageOptions from './ImageOptions';
import PetFusionColorwashSlider from './PetColorwashSlider';
import PointsOfInterestOverlay from './PointsOfInterest/PointsOfInterestOverlay';
import RegistrationShortcutHints from './RegistrationShortcutHints';
import ScaleDisplay from './ScaleDisplay';
import VoiSelectorOverlay from './VoiSelector';

const ViewportOverlayWrap = styled(Box)`
    width: 100%;
    height: 100%;
    position: relative;
    border: 1px solid ${({ theme }) => theme.palette.divider};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const ViewportOverlaySectionWrap = styled(Box)`
    position: absolute;
    z-index: 15;
`;

interface ViewportOverlayProps {
    view: PlaneView | null;
    viewportOrientation: Orientations;
    canvas: React.RefObject<HTMLCanvasElement | null>;
    canvas2D: React.RefObject<HTMLCanvasElement | null>;
    isActive: boolean;
}

const ViewportOverlay = ({
    view,
    viewportOrientation,
    canvas,
    canvas2D,
    isActive,
}: ViewportOverlayProps) => {
    const viewportOrientationOrder = useRecoilValue(
        viewportOrientationOrderState
    );
    const gridLayoutMode = useRecoilValue(gridLayoutModeState);
    const isMainViewport = viewportOrientationOrder[0] === viewportOrientation;
    const isPrimaryImageViewport = view?.type === PlaneViewType.Primary;
    const isGridLayoutModeDefault = gridLayoutMode === GridLayoutMode.Default;
    const [showFullscreen, setShowFullscreen] =
        useRecoilState(showFullscreenState);
    const doseVisibility = useRecoilValue(doseVisibilityState);
    const scaleDisplayEnabled = useRecoilValue(scaleDisplayEnabledState);

    const { isPetSecondary } = usePetImage();

    const showDoseSlider =
        isMainViewport && doseVisibility && isPrimaryImageViewport;
    const showPetSlider = isMainViewport && isPetSecondary;

    return (
        <ViewportOverlayWrap data-viewport-container="true">
            {showDoseSlider && (
                <ViewportOverlaySectionWrap
                    left={12}
                    top={8}
                    display="flex"
                    gap={3}
                >
                    <DoseColorwashSlider />
                </ViewportOverlaySectionWrap>
            )}

            {showPetSlider && (
                <ViewportOverlaySectionWrap
                    right={35}
                    top="10%"
                    sx={{ mb: '10px' }}
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                >
                    <PetFusionColorwashSlider />
                </ViewportOverlaySectionWrap>
            )}

            {scaleDisplayEnabled && (
                <ScaleDisplay view={view} canvasRef={canvas} />
            )}

            {isGridLayoutModeDefault && (
                <ViewportOverlaySectionWrap top={8} right={12}>
                    {isMainViewport ? (
                        <SwapToFullScreen
                            showFullscreen={showFullscreen}
                            setShowFullscreen={setShowFullscreen}
                        />
                    ) : (
                        <SwapToMain viewportOrientation={viewportOrientation} />
                    )}
                </ViewportOverlaySectionWrap>
            )}

            {isMainViewport && (
                <ViewportOverlaySectionWrap
                    bottom={8}
                    left={8}
                    display="grid"
                    gap={0.5}
                >
                    <RegistrationShortcutHints />
                    <ImageOptions />
                </ViewportOverlaySectionWrap>
            )}
            <LabelDirections viewportOrientation={viewportOrientation} />
            <ViewportOverlaySectionWrap bottom={8} right={8}>
                <CoordinateDisplay
                    viewportOrientation={viewportOrientation}
                    isActive={isActive}
                    isPrimaryImageViewport={isPrimaryImageViewport}
                />
            </ViewportOverlaySectionWrap>
            <CrosshairsComponent view={view} />
            <PointsOfInterestOverlay view={view} />
            <PlaneViewFrame view={view} canvas={canvas} canvas2D={canvas2D} />

            <VoiSelectorOverlay
                view={view}
                viewportOrientation={viewportOrientation}
            />
        </ViewportOverlayWrap>
    );
};

export default ViewportOverlay;
